import Collection from '@fluorescent/collection';
import { add, qs, qsa, remove } from '@fluorescent/dom';
import Delegate from 'ftdomdelegate';
import { Ajaxinate } from 'ajaxinate';

import { section } from '@/neon';
import productItem from '@/components/productItem';
import { AjaxinateShim } from '@/lib/shame';
import { emit, hydrate, on } from '@/neon/events';
import sidebar from '@/lib/collection/sidebar';
import flyout from '@/lib/filters/flyout';
import filtering from '@/lib/filters/filtering';
import {
  // emit
  removeFilters,
  removeRange,
  clearAll,
  updateFilters,
  // on
  filtersUpdated,
  filtersRemoved,
  rangeRemoved,
  everythingCleared,
} from '@/lib/collection/events';

const selectors = {
  products: '.product-item',
  infiniteScrollContainer: '.collection__infinite-container',
  infiniteScrollTrigger: '.collection__infinite-trigger',
  sidebar: '[data-sidebar]',
  partial: '[data-partial]',
  sort: '[data-sort]',
  filterFlyoutForm: '[data-filter-form-flyout]',
};

section('collection', {
  infiniteScroll: null,
  onLoad() {
    const { collectionItemCount, paginationType } = this.container.dataset;

    if (!parseInt(collectionItemCount)) return;

    this.paginationType = paginationType;
    this.paginated = this.paginationType === 'paginated';
    this.infiniteScrollTrigger = qs(
      selectors.infiniteScrollTrigger,
      this.container
    );

    this.collection = Collection(window.location.href);
    this.sidebar = sidebar(qs(selectors.sidebar, this.container));
    this.flyout = flyout(this.container);

    this.filterForm = qs('[data-filter-form]', this.container);

    if (this.filterForm) {
      this._initFiltering();
    }

    // Set initial evx state from collection url object
    hydrate(this.collection.getState());

    this.partial = qs(selectors.partial, this.container);

    this._initInfiniteScroll();
    this._initProductItems();
  },

  _initFiltering() {
    // collection filters
    this.filtering = filtering(this.container);

    // Set initial evx state from collection url object
    hydrate(this.filtering.getState());

    this.partial = qs(selectors.partial, this.container);

    this.subscriptions = [
      filtersRemoved((_, { target }) => {
        this.filtering.removeFilters(target, data => {
          this._renderView(data.url);
          hydrate(data)();
        });
      }),

      rangeRemoved(() => {
        this.filtering.removeRange(data => {
          this._renderView(data.url);
          hydrate(data)();
        });
      }),

      filtersUpdated((_, { target }) => {
        this.filtering.filtersUpdated(target, data => {
          this._renderView(data.url);
          hydrate(data)();
        });
      }),

      everythingCleared(() => {
        this.filtering.clearAll(data => {
          this._renderView(data.url);
          hydrate(data)();
        });
      }),
    ];

    this.delegate = new Delegate(this.partial);

    this.delegate.on('click', '[data-remove-filter]', e => {
      e.preventDefault();
      removeFilters([e.target]);
    });

    this.delegate.on('click', '[data-remove-range]', e => {
      e.preventDefault();
      removeRange();
    });

    this.delegate.on('click', '[data-clear]', e => {
      e.preventDefault();
      clearAll();
    });

    this.collectionUpdatedHanlder = on('collection:updated', () => {
      this.productItems.forEach(item => item.unload());
      this._initProductItems();
    });
  },

  _initInfiniteScroll() {
    if (this.paginated) return;
    const infiniteScrollOptions = {
      container: selectors.infiniteScrollContainer,
      pagination: selectors.infiniteScrollTrigger,
      loadingText: 'Loading...',
      callback: () => emit('collection:updated'),
    };

    if (this.paginationType === 'click') {
      infiniteScrollOptions.method = 'click';
    }

    AjaxinateShim(Ajaxinate);

    this.infiniteScroll = new Ajaxinate(infiniteScrollOptions);
  },

  _initProductItems() {
    const products = this.container.querySelectorAll(selectors.products);
    this.productItems = [];

    products.forEach(element => {
      this.productItems.push(productItem(element));
    });
  },

  _renderView(searchParams) {
    const url = `${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${searchParams}`;
    const loading = qs('.collection__loading', this.container);

    add(loading, 'is-active');

    fetch(url, { credentials: 'include' })
      .then(res => res.text())
      .then(res => {
        this._updateURLHash(searchParams);
        const doc = new window.DOMParser().parseFromString(res, 'text/html');
        const contents = qs(selectors.partial, doc).innerHTML;
        this.partial.innerHTML = contents;

        const filterFlyoutForm = qs(selectors.filterFlyoutForm, doc).innerHTML;
        qs(selectors.filterFlyoutForm, this.container).innerHTML =
          filterFlyoutForm;

        if (!this.paginated) {
          this.infiniteScrollTrigger.innerHTML = '';

          this._initInfiniteScroll();
        }

        remove(loading, 'is-active');
        emit('collection:updated', { doc: doc });
      });
  },

  _updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  },

  onUnload() {
    this.infiniteScroll && this.infiniteScroll.destroy();
    this.productItems && this.productItems.forEach(item => item.unload());

    this.sidebar && this.sidebar();
    this.flyout && this.flyout();
    this.filtering && this.filtering.unload();

    this.delegate && this.delegate.off();
    this.subscriptions && this.subscriptions.forEach(unsubscribe => unsubscribe());
    this.collectionUpdatedHanlder && this.collectionUpdatedHanlder();
  },
});
